/**
 * src/pages/AnalyticsReportCreate.js
 * Report builder page — wizard & form modes for create and edit.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyDefinition() {
  return {
    version: 1,
    created_against_config_version: null,
    steps: [],
    links: [],
    self_links: [],
    metrics: { counts: true, conversion_rates: true, time_between_steps: { enabled: true, aggregations: ['avg', 'median', 'min', 'max'] } },
    display: { funnel_style: 'horizontal_bars' }
  };
}

function emptyTimeSeriesDefinition() {
  return {
    version: 1,
    type: 'time_series',
    series: [],
    granularity: 'day',
    display: { chart_type: 'line' }
  };
}

function emptyDurationDefinition() {
  return {
    version: 1,
    type: 'duration',
    transitions: [],
    percentiles: [50, 75, 90, 95],
    histogram: { enabled: true, bucket_count: 10 },
    display: { chart_type: 'bar' }
  };
}

function emptyActiveDefinition() {
  return {
    version: 1,
    type: 'active',
    steps: [],
    links: [],
    stale_threshold: { value: 3, unit: 'days' }
  };
}

function emptyComparisonDefinition() {
  return {
    version: 1,
    type: 'comparison',
    base_type: 'funnel',
    base_definition: {
      steps: [],
      links: [],
      metrics: { counts: true, conversion_rates: true, time_between_steps: { enabled: false } },
      display: { funnel_style: 'horizontal_bars' }
    },
    scenarios: {
      a: { label: '', from: '', to: '', config_version: '' },
      b: { label: '', from: '', to: '', config_version: '' }
    }
  };
}

function formatDateOffset(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split('T')[0];
}

function secondsToDisplay(seconds) {
  if (!seconds) return { value: '', unit: 'hours' };
  if (seconds % 86400 === 0) return { value: seconds / 86400, unit: 'days' };
  if (seconds % 3600 === 0) return { value: seconds / 3600, unit: 'hours' };
  return { value: seconds / 60, unit: 'minutes' };
}

function displayToSeconds(value, unit) {
  const n = parseFloat(value);
  if (isNaN(n) || n <= 0) return undefined;
  switch (unit) {
    case 'days': return n * 86400;
    case 'hours': return n * 3600;
    case 'minutes': return n * 60;
    default: return n * 3600;
  }
}

function sharedIdentifiers(stepA, stepB, availableSteps) {
  if (!availableSteps || !stepA || !stepB) return [];
  const idsA = getStepIdentifiers(stepA, availableSteps);
  const idsB = getStepIdentifiers(stepB, availableSteps);
  return idsA.filter(id => idsB.includes(id));
}

function getStepIdentifiers(step, availableSteps) {
  if (!step || !availableSteps) return [];
  for (const p of availableSteps) {
    if (p.process_id === step.process_id) {
      for (const s of p.steps) {
        if (s.step_id === step.step_id) return s.identifiers || [];
      }
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Wizard Builder
// ---------------------------------------------------------------------------

function WizardBuilder({ availableSteps, definition, onChange, onSave, onCancel, saving, error: saveError }) {
  const [wizardStep, setWizardStep] = useState(0);
  const def = definition;

  const wizardSteps = [
    'Name & Description',
    'Select Steps',
    'Define Links',
    'Choose Metrics',
    'Review & Save'
  ];

  function updateDef(patch) {
    onChange({ ...def, ...patch });
  }

  function canNext() {
    if (wizardStep === 0) return (def._name || '').trim().length > 0;
    if (wizardStep === 1) return def.steps.length >= 2;
    if (wizardStep === 2) {
      if (def.links.length === 0) return false;
      return def.links.every(l => l.identifier);
    }
    return true;
  }

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Create Report — Wizard</span>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          Step {wizardStep + 1} of {wizardSteps.length}
        </span>
      </div>
      <div className="card-body">
        {/* Progress bar */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
          {wizardSteps.map((label, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                height: '4px', borderRadius: '2px', marginBottom: '6px',
                background: i <= wizardStep ? 'var(--accent)' : 'var(--border)'
              }} />
              <span style={{ fontSize: '11px', color: i <= wizardStep ? 'var(--accent)' : 'var(--text-tertiary)' }}>
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Step 0: Name & Description */}
        {wizardStep === 0 && (
          <div>
            <div className="form-group">
              <label className="form-label">Report Name *</label>
              <input className="form-input" type="text" value={def._name || ''}
                onChange={e => updateDef({ _name: e.target.value })}
                placeholder="e.g. Quote to Invoice Funnel" maxLength={200} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Description <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
              <textarea className="form-input" value={def._description || ''}
                onChange={e => updateDef({ _description: e.target.value })}
                placeholder="What does this report analyze?"
                rows={3} style={{ resize: 'vertical' }} />
            </div>
          </div>
        )}

        {/* Step 1: Select Steps */}
        {wizardStep === 1 && (
          <div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Select at least 2 steps and arrange them in the order of your funnel.
            </p>
            {def.steps.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                {def.steps.map((step, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                    background: 'var(--accent-subtle)', borderRadius: '6px', marginBottom: '6px'
                  }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--accent)', minWidth: '20px' }}>{i + 1}</span>
                    <span style={{ flex: 1, fontSize: '13px' }}>
                      {step.label || step.step_id}
                      <span style={{ color: 'var(--text-tertiary)', marginLeft: '8px', fontSize: '12px' }}>{step.process_id}</span>
                    </span>
                    <button className="btn btn-ghost btn-sm" onClick={() => {
                      if (i > 0) {
                        const newSteps = [...def.steps];
                        [newSteps[i - 1], newSteps[i]] = [newSteps[i], newSteps[i - 1]];
                        updateDef({ steps: newSteps });
                      }
                    }} disabled={i === 0} style={{ padding: '2px 6px' }}>↑</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => {
                      if (i < def.steps.length - 1) {
                        const newSteps = [...def.steps];
                        [newSteps[i], newSteps[i + 1]] = [newSteps[i + 1], newSteps[i]];
                        updateDef({ steps: newSteps });
                      }
                    }} disabled={i === def.steps.length - 1} style={{ padding: '2px 6px' }}>↓</button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', padding: '2px 6px' }}
                      onClick={() => updateDef({ steps: def.steps.filter((_, j) => j !== i) })}>×</button>
                  </div>
                ))}
              </div>
            )}

            {(availableSteps || []).map(process => (
              <div key={process.process_id} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {process.process_name}
                </div>
                {process.steps.map(step => {
                  const alreadyAdded = def.steps.some(s => s.process_id === process.process_id && s.step_id === step.step_id);
                  return (
                    <button key={step.step_id} className="btn btn-ghost btn-sm"
                      style={{ marginRight: '6px', marginBottom: '4px', opacity: alreadyAdded ? 0.4 : 1 }}
                      disabled={alreadyAdded}
                      onClick={() => {
                        updateDef({
                          steps: [...def.steps, {
                            process_id: process.process_id,
                            step_id: step.step_id,
                            label: step.label
                          }]
                        });
                      }}>
                      + {step.label || step.step_id}
                      {step.source === 'config' && (
                        <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginLeft: '4px' }}>(no events yet)</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Step 2: Define Links */}
        {wizardStep === 2 && (
          <div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Define how adjacent steps are linked via shared identifiers.
            </p>
            {def.steps.length >= 2 && (() => {
              const links = [];
              for (let i = 0; i < def.steps.length - 1; i++) {
                const existing = def.links.find(l => l.from_index === i && l.to_index === i + 1);
                if (existing) {
                  links.push(existing);
                } else {
                  const shared = sharedIdentifiers(def.steps[i], def.steps[i + 1], availableSteps);
                  links.push({
                    from_index: i,
                    to_index: i + 1,
                    identifier: shared.length === 1 ? shared[0] : '',
                    cardinality: 'many-to-one'
                  });
                }
              }
              if (JSON.stringify(links) !== JSON.stringify(def.links)) {
                setTimeout(() => updateDef({ links }), 0);
              }

              return links.map((link, i) => {
                const fromStep = def.steps[link.from_index];
                const toStep = def.steps[link.to_index];
                const shared = sharedIdentifiers(fromStep, toStep, availableSteps);
                const allIds = [...new Set([
                  ...getStepIdentifiers(fromStep, availableSteps),
                  ...getStepIdentifiers(toStep, availableSteps)
                ])];

                return (
                  <div key={i} style={{
                    padding: '12px', background: 'var(--bg-secondary, #f9fafb)',
                    borderRadius: '6px', marginBottom: '10px', border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                      {fromStep.label || fromStep.step_id} → {toStep.label || toStep.step_id}
                    </div>
                    <div className="form-row" style={{ gap: '12px' }}>
                      <div className="form-group" style={{ flex: 2 }}>
                        <label className="form-label">Linking Identifier</label>
                        <select className="form-select" value={link.identifier}
                          onChange={e => {
                            const newLinks = [...links];
                            newLinks[i] = { ...newLinks[i], identifier: e.target.value };
                            updateDef({ links: newLinks });
                          }}>
                          <option value="">— Select identifier —</option>
                          {shared.length > 0 && (
                            <optgroup label="Shared identifiers">
                              {shared.map(id => <option key={id} value={id}>{id}</option>)}
                            </optgroup>
                          )}
                          {allIds.filter(id => !shared.includes(id)).length > 0 && (
                            <optgroup label="All identifiers">
                              {allIds.filter(id => !shared.includes(id)).map(id => <option key={id} value={id}>{id}</option>)}
                            </optgroup>
                          )}
                        </select>
                      </div>
                      <div className="form-group" style={{ flex: 1 }}>
                        <label className="form-label">Cardinality</label>
                        <select className="form-select" value={link.cardinality || 'many-to-one'}
                          onChange={e => {
                            const newLinks = [...links];
                            newLinks[i] = { ...newLinks[i], cardinality: e.target.value };
                            updateDef({ links: newLinks });
                          }}>
                          <option value="many-to-one">Many-to-one</option>
                          <option value="one-to-one">One-to-one</option>
                          <option value="one-to-many">One-to-many</option>
                        </select>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* Step 3: Choose Metrics */}
        {wizardStep === 3 && (
          <div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Choose which metrics to compute for this report.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={def.metrics?.counts !== false}
                onChange={e => updateDef({ metrics: { ...def.metrics, counts: e.target.checked } })} />
              Entity counts per step
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={def.metrics?.conversion_rates !== false}
                onChange={e => updateDef({ metrics: { ...def.metrics, conversion_rates: e.target.checked } })} />
              Conversion rates between steps
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={def.metrics?.time_between_steps?.enabled !== false}
                onChange={e => updateDef({
                  metrics: {
                    ...def.metrics,
                    time_between_steps: { ...def.metrics?.time_between_steps, enabled: e.target.checked, aggregations: ['avg', 'median', 'min', 'max'] }
                  }
                })} />
              Time between steps (avg, median, min, max)
            </label>
          </div>
        )}

        {/* Step 4: Review & Save */}
        {wizardStep === 4 && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>{def._name}</div>
              {def._description && <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{def._description}</div>}
            </div>
            <div style={{ fontSize: '13px', marginBottom: '12px' }}>
              <strong>Steps ({def.steps.length}):</strong>
              {def.steps.map((s, i) => (
                <div key={i} style={{ marginLeft: '16px', marginTop: '4px' }}>
                  {i + 1}. {s.label || s.step_id} <span style={{ color: 'var(--text-tertiary)' }}>({s.process_id})</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '13px', marginBottom: '12px' }}>
              <strong>Links ({def.links.length}):</strong>
              {def.links.map((l, i) => (
                <div key={i} style={{ marginLeft: '16px', marginTop: '4px' }}>
                  {def.steps[l.from_index]?.label || def.steps[l.from_index]?.step_id} →{' '}
                  {def.steps[l.to_index]?.label || def.steps[l.to_index]?.step_id} via <code>{l.identifier}</code>{' '}
                  <span style={{ color: 'var(--text-tertiary)' }}>({l.cardinality})</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '13px' }}>
              <strong>Metrics:</strong>{' '}
              {[
                def.metrics?.counts !== false && 'Counts',
                def.metrics?.conversion_rates !== false && 'Conversion rates',
                def.metrics?.time_between_steps?.enabled && 'Time between steps'
              ].filter(Boolean).join(', ') || 'None'}
            </div>
            {saveError && <div className="alert alert-error" style={{ marginTop: '16px' }}>{saveError}</div>}
          </div>
        )}

        {/* Navigation buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
          <div>
            <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {wizardStep > 0 && (
              <button className="btn btn-secondary" onClick={() => setWizardStep(wizardStep - 1)}>Back</button>
            )}
            {wizardStep < wizardSteps.length - 1 ? (
              <button className="btn btn-primary" onClick={() => setWizardStep(wizardStep + 1)} disabled={!canNext()}>
                Next
              </button>
            ) : (
              <button className="btn btn-primary" onClick={onSave} disabled={saving || !canNext()}>
                {saving ? 'Creating…' : 'Create Report'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form Builder (also used for edit)
// ---------------------------------------------------------------------------

function FormBuilder({ availableSteps, definition, onChange, onSave, onCancel, saving, error: saveError, isEdit }) {
  const def = definition;

  function updateDef(patch) {
    onChange({ ...def, ...patch });
  }

  function addStep(processId, stepId, label) {
    updateDef({ steps: [...def.steps, { process_id: processId, step_id: stepId, label }] });
  }

  function removeStep(index) {
    const newSteps = def.steps.filter((_, i) => i !== index);
    const newLinks = def.links
      .map(l => {
        let f = l.from_index, t = l.to_index;
        if (f === index || t === index) return null;
        if (f > index) f--;
        if (t > index) t--;
        return { ...l, from_index: f, to_index: t };
      })
      .filter(Boolean);
    updateDef({ steps: newSteps, links: newLinks });
  }

  function moveStep(index, direction) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= def.steps.length) return;
    const newSteps = [...def.steps];
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    const newLinks = def.links.map(l => {
      let f = l.from_index, t = l.to_index;
      if (f === index) f = newIndex;
      else if (f === newIndex) f = index;
      if (t === index) t = newIndex;
      else if (t === newIndex) t = index;
      return { ...l, from_index: f, to_index: t };
    });
    updateDef({ steps: newSteps, links: newLinks });
  }

  function updateLink(fromIndex, toIndex, field, value) {
    const linkIdx = def.links.findIndex(l => l.from_index === fromIndex && l.to_index === toIndex);
    if (linkIdx >= 0) {
      const newLinks = [...def.links];
      newLinks[linkIdx] = { ...newLinks[linkIdx], [field]: value };
      updateDef({ links: newLinks });
    } else {
      updateDef({ links: [...def.links, { from_index: fromIndex, to_index: toIndex, identifier: '', cardinality: 'many-to-one', [field]: value }] });
    }
  }

  const canSave = (def._name || '').trim().length > 0 && def.steps.length >= 2 &&
    def.links.length > 0 && def.links.every(l => l.identifier);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{isEdit ? 'Edit Report' : 'Create Report — Form'}</span>
      </div>
      <div className="card-body">
        {saveError && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{saveError}</div>}

        {/* Name & Description */}
        <div className="form-row" style={{ marginBottom: '20px' }}>
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Report Name *</label>
            <input className="form-input" type="text" value={def._name || ''}
              onChange={e => updateDef({ _name: e.target.value })}
              placeholder="e.g. Quote to Invoice Funnel" maxLength={200} />
          </div>
          <div className="form-group" style={{ flex: 3 }}>
            <label className="form-label">Description <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input className="form-input" type="text" value={def._description || ''}
              onChange={e => updateDef({ _description: e.target.value })}
              placeholder="What does this report analyze?" />
          </div>
        </div>

        {/* Steps */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Steps
          </div>
          {def.steps.map((step, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px',
              background: 'var(--accent-subtle)', borderRadius: '4px', marginBottom: '4px', fontSize: '13px'
            }}>
              <span style={{ fontWeight: '600', color: 'var(--accent)', minWidth: '18px' }}>{i + 1}</span>
              <span style={{ flex: 1 }}>
                {step.label || step.step_id}
                <span style={{ color: 'var(--text-tertiary)', marginLeft: '8px', fontSize: '12px' }}>{step.process_id}</span>
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => moveStep(i, -1)} disabled={i === 0} style={{ padding: '1px 5px' }}>↑</button>
              <button className="btn btn-ghost btn-sm" onClick={() => moveStep(i, 1)} disabled={i === def.steps.length - 1} style={{ padding: '1px 5px' }}>↓</button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', padding: '1px 5px' }} onClick={() => removeStep(i)}>×</button>
            </div>
          ))}
          <div style={{ marginTop: '8px' }}>
            <select className="form-select" style={{ maxWidth: '400px' }} value=""
              onChange={e => {
                if (!e.target.value) return;
                const [processId, stepId] = e.target.value.split('::');
                const process = (availableSteps || []).find(p => p.process_id === processId);
                const step = process?.steps.find(s => s.step_id === stepId);
                if (step) addStep(processId, stepId, step.label);
                e.target.value = '';
              }}>
              <option value="">+ Add step…</option>
              {(availableSteps || []).map(process => (
                <optgroup key={process.process_id} label={process.process_name}>
                  {process.steps.map(step => {
                    const already = def.steps.some(s => s.process_id === process.process_id && s.step_id === step.step_id);
                    const noEvents = step.source === 'config';
                    return (
                      <option key={step.step_id} value={`${process.process_id}::${step.step_id}`} disabled={already}>
                        {step.label || step.step_id}{already ? ' (added)' : ''}{noEvents ? ' — no events yet' : ''}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          </div>
        </div>

        {/* Links */}
        {def.steps.length >= 2 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
              Links
            </div>
            {def.steps.slice(0, -1).map((_, i) => {
              const fromStep = def.steps[i];
              const toStep = def.steps[i + 1];
              const link = def.links.find(l => l.from_index === i && l.to_index === i + 1) || {};
              const shared = sharedIdentifiers(fromStep, toStep, availableSteps);
              const allIds = [...new Set([
                ...getStepIdentifiers(fromStep, availableSteps),
                ...getStepIdentifiers(toStep, availableSteps)
              ])];

              return (
                <div key={i} style={{
                  display: 'flex', gap: '12px', alignItems: 'flex-end', padding: '8px 10px',
                  border: '1px solid var(--border)', borderRadius: '4px', marginBottom: '6px'
                }}>
                  <div style={{ flex: 0, fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', paddingBottom: '8px' }}>
                    {fromStep.label || fromStep.step_id} → {toStep.label || toStep.step_id}
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">Identifier</label>
                    <select className="form-select" value={link.identifier || ''}
                      onChange={e => updateLink(i, i + 1, 'identifier', e.target.value)}>
                      <option value="">— Select —</option>
                      {shared.length > 0 && (
                        <optgroup label="Shared">
                          {shared.map(id => <option key={id} value={id}>{id}</option>)}
                        </optgroup>
                      )}
                      {allIds.filter(id => !shared.includes(id)).length > 0 && (
                        <optgroup label="All">
                          {allIds.filter(id => !shared.includes(id)).map(id => <option key={id} value={id}>{id}</option>)}
                        </optgroup>
                      )}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 0, minWidth: '140px', marginBottom: 0 }}>
                    <label className="form-label">Cardinality</label>
                    <select className="form-select" value={link.cardinality || 'many-to-one'}
                      onChange={e => updateLink(i, i + 1, 'cardinality', e.target.value)}>
                      <option value="many-to-one">Many-to-one</option>
                      <option value="one-to-one">One-to-one</option>
                      <option value="one-to-many">One-to-many</option>
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Metrics */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Metrics
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px', cursor: 'pointer' }}>
            <input type="checkbox" checked={def.metrics?.counts !== false}
              onChange={e => updateDef({ metrics: { ...def.metrics, counts: e.target.checked } })} />
            Entity counts
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px', cursor: 'pointer' }}>
            <input type="checkbox" checked={def.metrics?.conversion_rates !== false}
              onChange={e => updateDef({ metrics: { ...def.metrics, conversion_rates: e.target.checked } })} />
            Conversion rates
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px', cursor: 'pointer' }}>
            <input type="checkbox" checked={def.metrics?.time_between_steps?.enabled !== false}
              onChange={e => updateDef({
                metrics: { ...def.metrics, time_between_steps: { ...def.metrics?.time_between_steps, enabled: e.target.checked, aggregations: ['avg', 'median', 'min', 'max'] } }
              })} />
            Time between steps
          </label>
        </div>

        {isEdit && (
          <div className="form-group">
            <label className="form-label">Change Note <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input className="form-input" type="text" value={def._changeNote || ''}
              onChange={e => updateDef({ _changeNote: e.target.value })}
              placeholder="What changed in this version?" />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving || !canSave}>
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Report')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Time-Series Form Builder
// ---------------------------------------------------------------------------

function TimeSeriesFormBuilder({ availableSteps, definition, onChange, onSave, onCancel, saving, error: saveError, isEdit }) {
  const def = definition;

  function updateDef(patch) {
    onChange({ ...def, ...patch });
  }

  function addSeries(processId, stepId, label) {
    updateDef({ series: [...(def.series || []), { process_id: processId, step_id: stepId, label }] });
  }

  function removeSeries(index) {
    updateDef({ series: def.series.filter((_, i) => i !== index) });
  }

  function updateSeriesLabel(index, label) {
    const newSeries = [...def.series];
    newSeries[index] = { ...newSeries[index], label };
    updateDef({ series: newSeries });
  }

  const canSave = (def._name || '').trim().length > 0 && (def.series || []).length > 0;

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{isEdit ? 'Edit Time-Series Report' : 'Create Time-Series Report'}</span>
      </div>
      <div className="card-body">
        {saveError && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{saveError}</div>}

        {/* Name & Description */}
        <div className="form-row" style={{ marginBottom: '20px' }}>
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Report Name *</label>
            <input className="form-input" type="text" value={def._name || ''}
              onChange={e => updateDef({ _name: e.target.value })}
              placeholder="e.g. Daily Event Volume" maxLength={200} />
          </div>
          <div className="form-group" style={{ flex: 3 }}>
            <label className="form-label">Description <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input className="form-input" type="text" value={def._description || ''}
              onChange={e => updateDef({ _description: e.target.value })}
              placeholder="What does this report track?" />
          </div>
        </div>

        {/* Data Series */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Data Series
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            Select process steps to chart. Each step becomes a separate line/bar on the chart.
          </p>
          {(def.series || []).map((s, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px',
              background: 'var(--accent-subtle)', borderRadius: '4px', marginBottom: '4px', fontSize: '13px'
            }}>
              <span style={{ fontWeight: '600', color: 'var(--accent)', minWidth: '18px' }}>{i + 1}</span>
              <input className="form-input" type="text" value={s.label || ''}
                onChange={e => updateSeriesLabel(i, e.target.value)}
                placeholder={s.step_id}
                style={{ flex: 1, padding: '4px 8px', fontSize: '13px' }} />
              <span style={{ color: 'var(--text-tertiary)', fontSize: '12px', whiteSpace: 'nowrap' }}>{s.process_id}</span>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', padding: '1px 5px' }}
                onClick={() => removeSeries(i)}>×</button>
            </div>
          ))}
          <div style={{ marginTop: '8px' }}>
            <select className="form-select" style={{ maxWidth: '400px' }} value=""
              onChange={e => {
                if (!e.target.value) return;
                const [processId, stepId] = e.target.value.split('::');
                const process = (availableSteps || []).find(p => p.process_id === processId);
                const step = process?.steps.find(s => s.step_id === stepId);
                if (step) addSeries(processId, stepId, step.label || stepId);
                e.target.value = '';
              }}>
              <option value="">+ Add series…</option>
              {(availableSteps || []).map(process => (
                <optgroup key={process.process_id} label={process.process_name}>
                  {process.steps.map(step => {
                    const already = (def.series || []).some(s => s.process_id === process.process_id && s.step_id === step.step_id);
                    return (
                      <option key={step.step_id} value={`${process.process_id}::${step.step_id}`} disabled={already}>
                        {step.label || step.step_id}{already ? ' (added)' : ''}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          </div>
        </div>

        {/* Granularity */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Granularity
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {['hour', 'day', 'week', 'month'].map(g => (
              <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="radio" name="granularity" value={g}
                  checked={def.granularity === g}
                  onChange={() => updateDef({ granularity: g })} />
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </label>
            ))}
          </div>
        </div>

        {/* Chart Type */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Chart Type
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[{ value: 'line', label: 'Line' }, { value: 'bar', label: 'Bar' }, { value: 'area', label: 'Area' }].map(ct => (
              <label key={ct.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="radio" name="chart_type" value={ct.value}
                  checked={(def.display?.chart_type || 'line') === ct.value}
                  onChange={() => updateDef({ display: { ...def.display, chart_type: ct.value } })} />
                {ct.label}
              </label>
            ))}
          </div>
        </div>

        {isEdit && (
          <div className="form-group">
            <label className="form-label">Change Note <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input className="form-input" type="text" value={def._changeNote || ''}
              onChange={e => updateDef({ _changeNote: e.target.value })}
              placeholder="What changed in this version?" />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving || !canSave}>
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Report')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Duration / SLA Form Builder
// ---------------------------------------------------------------------------

function DurationFormBuilder({ availableSteps, definition, onChange, onSave, onCancel, saving, error: saveError, isEdit }) {
  const def = definition;

  function updateDef(patch) {
    onChange({ ...def, ...patch });
  }

  function addTransition() {
    updateDef({
      transitions: [...(def.transitions || []), {
        from_step: { process_id: '', step_id: '', label: '' },
        to_step: { process_id: '', step_id: '', label: '' },
        identifier: '',
        label: '',
        sla: {},
        _warning: { value: '', unit: 'hours' },
        _breach: { value: '', unit: 'hours' }
      }]
    });
  }

  function removeTransition(index) {
    updateDef({ transitions: def.transitions.filter((_, i) => i !== index) });
  }

  function updateTransition(index, patch) {
    const updated = [...def.transitions];
    updated[index] = { ...updated[index], ...patch };
    updateDef({ transitions: updated });
  }

  function setTransitionStep(index, field, processId, stepId) {
    const process = (availableSteps || []).find(p => p.process_id === processId);
    const step = process?.steps.find(s => s.step_id === stepId);
    const stepObj = { process_id: processId, step_id: stepId, label: step?.label || stepId };
    const patch = { [field]: stepObj };
    // Clear identifier if steps changed
    const t = def.transitions[index];
    const other = field === 'from_step' ? t.to_step : t.from_step;
    if (other?.process_id && other?.step_id) {
      const shared = sharedIdentifiers(
        field === 'from_step' ? stepObj : t.from_step,
        field === 'from_step' ? t.to_step : stepObj,
        availableSteps
      );
      if (shared.length > 0 && !shared.includes(t.identifier)) {
        patch.identifier = shared[0];
      }
    }
    updateTransition(index, patch);
  }

  function updateSlaField(index, field, value, unit) {
    const t = def.transitions[index];
    const displayKey = field === 'warning' ? '_warning' : '_breach';
    const slaKey = field === 'warning' ? 'warning_seconds' : 'breach_seconds';
    const sla = { ...(t.sla || {}) };
    sla[slaKey] = displayToSeconds(value, unit);
    updateTransition(index, { sla, [displayKey]: { value, unit } });
  }

  function togglePercentile(p) {
    const current = def.percentiles || [50, 75, 90, 95];
    const next = current.includes(p) ? current.filter(x => x !== p) : [...current, p].sort((a, b) => a - b);
    updateDef({ percentiles: next });
  }

  const canSave = (def._name || '').trim().length > 0
    && (def.transitions || []).length > 0
    && def.transitions.every(t =>
      t.from_step?.process_id && t.from_step?.step_id
      && t.to_step?.process_id && t.to_step?.step_id
      && t.identifier
    );

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{isEdit ? 'Edit Duration / SLA Report' : 'Create Duration / SLA Report'}</span>
      </div>
      <div className="card-body">
        {saveError && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{saveError}</div>}

        {/* Name & Description */}
        <div className="form-row" style={{ marginBottom: '20px' }}>
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Report Name *</label>
            <input className="form-input" type="text" value={def._name || ''}
              onChange={e => updateDef({ _name: e.target.value })}
              placeholder="e.g. Invoice SLA Report" maxLength={200} />
          </div>
          <div className="form-group" style={{ flex: 3 }}>
            <label className="form-label">Description <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input className="form-input" type="text" value={def._description || ''}
              onChange={e => updateDef({ _description: e.target.value })}
              placeholder="What does this report track?" />
          </div>
        </div>

        {/* Transitions */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Transitions
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            Define step pairs to measure duration between. Each transition can have its own SLA thresholds.
          </p>

          {(def.transitions || []).map((t, i) => {
            const sharedIds = (t.from_step?.process_id && t.to_step?.process_id)
              ? sharedIdentifiers(t.from_step, t.to_step, availableSteps)
              : [];

            return (
              <div key={i} style={{
                border: '1px solid var(--border)', borderRadius: '6px', padding: '12px',
                marginBottom: '10px', background: 'var(--bg-secondary)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--accent)' }}>Transition {i + 1}</span>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', padding: '1px 5px' }}
                    onClick={() => removeTransition(i)}>Remove</button>
                </div>

                {/* From / To step selectors */}
                <div className="form-row" style={{ marginBottom: '8px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: '11px' }}>From Step *</label>
                    <select className="form-select" value={t.from_step?.process_id && t.from_step?.step_id ? `${t.from_step.process_id}::${t.from_step.step_id}` : ''}
                      onChange={e => {
                        if (!e.target.value) return;
                        const [pid, sid] = e.target.value.split('::');
                        setTransitionStep(i, 'from_step', pid, sid);
                      }}>
                      <option value="">Select step…</option>
                      {(availableSteps || []).map(p => (
                        <optgroup key={p.process_id} label={p.process_name}>
                          {p.steps.map(s => (
                            <option key={s.step_id} value={`${p.process_id}::${s.step_id}`}>
                              {s.label || s.step_id}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', padding: '0 4px 8px', fontSize: '13px', color: 'var(--text-tertiary)' }}>→</div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: '11px' }}>To Step *</label>
                    <select className="form-select" value={t.to_step?.process_id && t.to_step?.step_id ? `${t.to_step.process_id}::${t.to_step.step_id}` : ''}
                      onChange={e => {
                        if (!e.target.value) return;
                        const [pid, sid] = e.target.value.split('::');
                        setTransitionStep(i, 'to_step', pid, sid);
                      }}>
                      <option value="">Select step…</option>
                      {(availableSteps || []).map(p => (
                        <optgroup key={p.process_id} label={p.process_name}>
                          {p.steps.map(s => (
                            <option key={s.step_id} value={`${p.process_id}::${s.step_id}`}>
                              {s.label || s.step_id}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Identifier + Label */}
                <div className="form-row" style={{ marginBottom: '8px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: '11px' }}>Linking Identifier *</label>
                    <select className="form-select" value={t.identifier || ''}
                      onChange={e => updateTransition(i, { identifier: e.target.value })}>
                      <option value="">{sharedIds.length === 0 ? 'Select steps first' : 'Select identifier…'}</option>
                      {sharedIds.map(id => (
                        <option key={id} value={id}>{id}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: '11px' }}>Label <span style={{ fontWeight: '400' }}>(optional)</span></label>
                    <input className="form-input" type="text" value={t.label || ''}
                      onChange={e => updateTransition(i, { label: e.target.value })}
                      placeholder={`${t.from_step?.label || 'Step A'} → ${t.to_step?.label || 'Step B'}`} />
                  </div>
                </div>

                {/* SLA thresholds */}
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: '11px' }}>Warning Threshold <span style={{ fontWeight: '400' }}>(optional)</span></label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input className="form-input" type="number" min="0" step="any"
                        value={t._warning?.value ?? secondsToDisplay(t.sla?.warning_seconds).value}
                        onChange={e => updateSlaField(i, 'warning', e.target.value, t._warning?.unit || 'hours')}
                        style={{ flex: 1 }} placeholder="e.g. 24" />
                      <select className="form-select" style={{ width: '100px' }}
                        value={t._warning?.unit ?? secondsToDisplay(t.sla?.warning_seconds).unit}
                        onChange={e => updateSlaField(i, 'warning', t._warning?.value ?? secondsToDisplay(t.sla?.warning_seconds).value, e.target.value)}>
                        <option value="minutes">min</option>
                        <option value="hours">hours</option>
                        <option value="days">days</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: '11px' }}>Breach Threshold <span style={{ fontWeight: '400' }}>(optional)</span></label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input className="form-input" type="number" min="0" step="any"
                        value={t._breach?.value ?? secondsToDisplay(t.sla?.breach_seconds).value}
                        onChange={e => updateSlaField(i, 'breach', e.target.value, t._breach?.unit || 'hours')}
                        style={{ flex: 1 }} placeholder="e.g. 48" />
                      <select className="form-select" style={{ width: '100px' }}
                        value={t._breach?.unit ?? secondsToDisplay(t.sla?.breach_seconds).unit}
                        onChange={e => updateSlaField(i, 'breach', t._breach?.value ?? secondsToDisplay(t.sla?.breach_seconds).value, e.target.value)}>
                        <option value="minutes">min</option>
                        <option value="hours">hours</option>
                        <option value="days">days</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <button className="btn btn-ghost" onClick={addTransition} style={{ fontSize: '13px' }}>
            + Add Transition
          </button>
        </div>

        {/* Percentiles */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Percentiles
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[50, 75, 90, 95, 99].map(p => (
              <label key={p} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={(def.percentiles || []).includes(p)}
                  onChange={() => togglePercentile(p)} />
                p{p}
              </label>
            ))}
          </div>
        </div>

        {/* Chart Type */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Chart Type
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[{ value: 'bar', label: 'Summary Bars' }, { value: 'histogram', label: 'Histogram' }].map(ct => (
              <label key={ct.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="radio" name="duration_chart_type" value={ct.value}
                  checked={(def.display?.chart_type || 'bar') === ct.value}
                  onChange={() => updateDef({ display: { ...def.display, chart_type: ct.value } })} />
                {ct.label}
              </label>
            ))}
          </div>
        </div>

        {isEdit && (
          <div className="form-group">
            <label className="form-label">Change Note <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input className="form-input" type="text" value={def._changeNote || ''}
              onChange={e => updateDef({ _changeNote: e.target.value })}
              placeholder="What changed in this version?" />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving || !canSave}>
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Report')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active / WIP Form Builder
// ---------------------------------------------------------------------------

function ActiveFormBuilder({ availableSteps, definition, onChange, onSave, onCancel, saving, error, isEdit }) {
  const def = definition || emptyActiveDefinition();
  const updateDef = patch => onChange({ ...def, ...patch });
  const saveError = error;

  function addStep() {
    updateDef({ steps: [...(def.steps || []), { process_id: '', step_id: '', label: '' }] });
  }

  function updateStep(index, patch) {
    const steps = [...(def.steps || [])];
    steps[index] = { ...steps[index], ...patch };
    updateDef({ steps });
  }

  function removeStep(index) {
    const steps = (def.steps || []).filter((_, i) => i !== index);
    // Rebuild links for remaining adjacent steps
    const links = [];
    for (let i = 0; i < steps.length - 1; i++) {
      const existingLink = (def.links || []).find(l => l.from_index === i && l.to_index === i + 1);
      links.push({
        from_index: i,
        to_index: i + 1,
        identifier: existingLink?.identifier || ''
      });
    }
    // Update terminal_steps indexes
    const newTerminal = (def.terminal_steps || [])
      .filter(idx => idx !== index)
      .map(idx => idx > index ? idx - 1 : idx);
    updateDef({ steps, links, terminal_steps: newTerminal.length > 0 ? newTerminal : undefined });
  }

  function moveStep(index, direction) {
    const steps = [...(def.steps || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
    // Rebuild links
    const links = [];
    for (let i = 0; i < steps.length - 1; i++) {
      links.push({ from_index: i, to_index: i + 1, identifier: '' });
    }
    // Update terminal_steps to follow swapped steps
    const newTerminal = (def.terminal_steps || []).map(idx => {
      if (idx === index) return newIndex;
      if (idx === newIndex) return index;
      return idx;
    });
    updateDef({ steps, links, terminal_steps: newTerminal.length > 0 ? newTerminal : undefined });
  }

  function setStepFromSelect(index, value) {
    if (!value) return;
    const [processId, stepId] = value.split('::');
    const process = (availableSteps || []).find(p => p.process_id === processId);
    const step = process?.steps.find(s => s.step_id === stepId);
    updateStep(index, { process_id: processId, step_id: stepId, label: step?.label || stepId });

    // Auto-generate links for adjacent steps
    const steps = [...(def.steps || [])];
    steps[index] = { ...steps[index], process_id: processId, step_id: stepId, label: step?.label || stepId };
    const links = [];
    for (let i = 0; i < steps.length - 1; i++) {
      const existingLink = (def.links || []).find(l => l.from_index === i && l.to_index === i + 1);
      let identifier = existingLink?.identifier || '';
      // Auto-pick identifier if both steps are set
      if (steps[i].process_id && steps[i + 1].process_id && !identifier) {
        const shared = sharedIdentifiers(steps[i], steps[i + 1], availableSteps);
        if (shared.length > 0) identifier = shared[0];
      }
      links.push({ from_index: i, to_index: i + 1, identifier });
    }
    updateDef({ steps, links });
  }

  function updateLinkIdentifier(linkIndex, identifier) {
    const links = [...(def.links || [])];
    links[linkIndex] = { ...links[linkIndex], identifier };
    updateDef({ links });
  }

  const canSave = (def._name || '').trim().length > 0
    && (def.steps || []).length >= 2
    && def.steps.every(s => s.process_id && s.step_id)
    && (def.links || []).length > 0
    && def.links.every(l => l.identifier);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{isEdit ? 'Edit Active / WIP Report' : 'Create Active / WIP Report'}</span>
      </div>
      <div className="card-body">
        {saveError && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{saveError}</div>}

        {/* Name & Description */}
        <div className="form-row" style={{ marginBottom: '20px' }}>
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Report Name *</label>
            <input className="form-input" type="text" value={def._name || ''}
              onChange={e => updateDef({ _name: e.target.value })}
              placeholder="e.g. Invoice Pipeline WIP" maxLength={200} />
          </div>
          <div className="form-group" style={{ flex: 3 }}>
            <label className="form-label">Description <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input className="form-input" type="text" value={def._description || ''}
              onChange={e => updateDef({ _description: e.target.value })}
              placeholder="What does this report track?" />
          </div>
        </div>

        {/* Steps */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Process Steps
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            Define the steps in order. Check "Terminal" on steps where the process ends — entities reaching any terminal step are considered complete. If none are checked, the last step is used.
          </p>

          {(def.steps || []).map((s, i) => (
            <div key={i} style={{
              border: '1px solid var(--border)', borderRadius: '6px', padding: '10px 12px',
              marginBottom: '8px', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', gap: '8px'
            }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-tertiary)', minWidth: '24px' }}>{i + 1}.</span>
              <select className="form-select" style={{ flex: 1 }}
                value={s.process_id && s.step_id ? `${s.process_id}::${s.step_id}` : ''}
                onChange={e => setStepFromSelect(i, e.target.value)}>
                <option value="">Select step…</option>
                {(availableSteps || []).map(p => (
                  <optgroup key={p.process_id} label={p.process_name}>
                    {p.steps.map(st => (
                      <option key={st.step_id} value={`${p.process_id}::${st.step_id}`}>
                        {st.label || st.step_id}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                <input type="checkbox"
                  checked={(def.terminal_steps || []).includes(i)}
                  onChange={e => {
                    const current = def.terminal_steps || [];
                    const next = e.target.checked
                      ? [...current, i]
                      : current.filter(idx => idx !== i);
                    updateDef({ terminal_steps: next.length > 0 ? next : undefined });
                  }}
                />
                Terminal
              </label>
              <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }}
                onClick={() => moveStep(i, -1)} disabled={i === 0} title="Move up">↑</button>
              <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }}
                onClick={() => moveStep(i, 1)} disabled={i === (def.steps || []).length - 1} title="Move down">↓</button>
              <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', color: 'var(--danger)' }}
                onClick={() => removeStep(i)} title="Remove">×</button>
            </div>
          ))}

          <button className="btn btn-ghost" onClick={addStep} style={{ fontSize: '13px' }}>
            + Add Step
          </button>
        </div>

        {/* Links (auto-generated, just need identifier selection) */}
        {(def.links || []).length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
              Step Links
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Select the identifier that links entities across each step pair.
            </p>

            {(def.links || []).map((link, i) => {
              const fromStep = (def.steps || [])[link.from_index];
              const toStep = (def.steps || [])[link.to_index];
              const sharedIds = (fromStep?.process_id && toStep?.process_id)
                ? sharedIdentifiers(fromStep, toStep, availableSteps)
                : [];

              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 12px', marginBottom: '6px',
                  border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg-secondary)'
                }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    {fromStep?.label || `Step ${link.from_index + 1}`}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>→</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    {toStep?.label || `Step ${link.to_index + 1}`}
                  </span>
                  <select className="form-select" style={{ flex: 1, maxWidth: '200px' }}
                    value={link.identifier || ''}
                    onChange={e => updateLinkIdentifier(i, e.target.value)}>
                    <option value="">{sharedIds.length === 0 ? 'No shared identifiers' : 'Select identifier…'}</option>
                    {sharedIds.map(id => (
                      <option key={id} value={id}>{id}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}

        {/* Stale Threshold */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Stale Threshold
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            Entities waiting longer than this are flagged as stale.
          </p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', maxWidth: '300px' }}>
            <input className="form-input" type="number" min="1" step="1"
              value={def.stale_threshold?.value || 3}
              onChange={e => updateDef({ stale_threshold: { ...def.stale_threshold, value: parseInt(e.target.value) || 1 } })}
              style={{ width: '80px' }} />
            <select className="form-select" style={{ width: '100px' }}
              value={def.stale_threshold?.unit || 'days'}
              onChange={e => updateDef({ stale_threshold: { ...def.stale_threshold, unit: e.target.value } })}>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </select>
          </div>
        </div>

        {isEdit && (
          <div className="form-group">
            <label className="form-label">Change Note <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input className="form-input" type="text" value={def._changeNote || ''}
              onChange={e => updateDef({ _changeNote: e.target.value })}
              placeholder="What changed in this version?" />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving || !canSave}>
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Report')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparison Form Builder
// ---------------------------------------------------------------------------

function ComparisonFormBuilder({ availableSteps, definition, onChange, onSave, onCancel, saving, error: saveError, isEdit, configVersions }) {
  const def = definition;
  const baseType = def.base_type || 'funnel';
  const baseDef = def.base_definition || {};
  const scenarios = def.scenarios || { a: {}, b: {} };

  function updateBaseType(newType) {
    const newBaseDef = newType === 'time_series'
      ? { series: [], granularity: 'day', display: { chart_type: 'line' } }
      : { steps: [], links: [], metrics: { counts: true, conversion_rates: true, time_between_steps: { enabled: false } }, display: { funnel_style: 'horizontal_bars' } };
    onChange({ ...def, base_type: newType, base_definition: newBaseDef });
  }

  function updateBaseDef(updates) {
    onChange({ ...def, base_definition: { ...baseDef, ...updates } });
  }

  function updateScenario(key, updates) {
    onChange({ ...def, scenarios: { ...scenarios, [key]: { ...scenarios[key], ...updates } } });
  }

  function applyPreset(preset) {
    if (preset === 'last7') {
      updateScenario('a', { label: 'Previous 7 days', from: formatDateOffset(14), to: formatDateOffset(7) });
      // Need to set B separately since updateScenario uses stale def
      onChange(prev => ({
        ...prev,
        scenarios: {
          a: { ...prev.scenarios.a, label: 'Previous 7 days', from: formatDateOffset(14), to: formatDateOffset(7) },
          b: { ...prev.scenarios.b, label: 'Last 7 days', from: formatDateOffset(7), to: formatDateOffset(0) }
        }
      }));
    } else if (preset === 'last30') {
      onChange(prev => ({
        ...prev,
        scenarios: {
          a: { ...prev.scenarios.a, label: 'Previous 30 days', from: formatDateOffset(60), to: formatDateOffset(30) },
          b: { ...prev.scenarios.b, label: 'Last 30 days', from: formatDateOffset(30), to: formatDateOffset(0) }
        }
      }));
    }
  }

  // Flatten available steps for dropdowns
  const allSteps = [];
  if (availableSteps) {
    for (const p of availableSteps) {
      for (const s of p.steps) {
        allSteps.push({ process_id: p.process_id, process_name: p.process_name, step_id: s.step_id, label: s.label, identifiers: s.identifiers || [] });
      }
    }
  }

  // Funnel base: step + link management
  function addFunnelStep(step) {
    const steps = [...(baseDef.steps || []), { process_id: step.process_id, step_id: step.step_id, label: step.label }];
    const links = [...(baseDef.links || [])];
    // Auto-link to previous step if shared identifiers exist
    if (steps.length > 1) {
      const prevIdx = steps.length - 2;
      const newIdx = steps.length - 1;
      const prevStep = steps[prevIdx];
      const shared = sharedIdentifiers(prevStep, steps[newIdx], availableSteps);
      if (shared.length > 0) {
        links.push({ from_index: prevIdx, to_index: newIdx, identifier: shared[0] });
      }
    }
    updateBaseDef({ steps, links });
  }

  function removeFunnelStep(idx) {
    const steps = baseDef.steps.filter((_, i) => i !== idx);
    const links = (baseDef.links || [])
      .filter(l => l.from_index !== idx && l.to_index !== idx)
      .map(l => ({
        ...l,
        from_index: l.from_index > idx ? l.from_index - 1 : l.from_index,
        to_index: l.to_index > idx ? l.to_index - 1 : l.to_index
      }));
    updateBaseDef({ steps, links });
  }

  function updateLinkIdentifier(linkIdx, identifier) {
    const links = [...(baseDef.links || [])];
    links[linkIdx] = { ...links[linkIdx], identifier };
    updateBaseDef({ links });
  }

  // Time-series base: series management
  function addSeries(step) {
    const series = [...(baseDef.series || []), { process_id: step.process_id, step_id: step.step_id, label: step.label }];
    updateBaseDef({ series });
  }

  function removeSeries(idx) {
    updateBaseDef({ series: baseDef.series.filter((_, i) => i !== idx) });
  }

  const canSave = def._name?.trim() &&
    (baseType === 'funnel' ? (baseDef.steps?.length >= 1) : (baseDef.series?.length >= 1)) &&
    scenarios.a?.label?.trim() && scenarios.b?.label?.trim() &&
    (scenarios.a.from || scenarios.a.to || scenarios.a.config_version) &&
    (scenarios.b.from || scenarios.b.to || scenarios.b.config_version);

  function renderScenarioFields(key, scenario) {
    return (
      <div className="card" style={{ marginBottom: '12px' }}>
        <div className="card-body">
          <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.05em', marginBottom: '8px' }}>
            Scenario {key.toUpperCase()}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 120px' }}>
              <label className="form-label">Label</label>
              <input className="form-input" value={scenario.label || ''} placeholder={key === 'a' ? 'e.g. Previous period' : 'e.g. Current period'}
                onChange={e => updateScenario(key, { label: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 120px' }}>
              <label className="form-label">From</label>
              <input className="form-input" type="date" value={scenario.from || ''} onChange={e => updateScenario(key, { from: e.target.value })} />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <label className="form-label">To</label>
              <input className="form-input" type="date" value={scenario.to || ''} onChange={e => updateScenario(key, { to: e.target.value })} />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <label className="form-label">Config Version</label>
              <select className="form-select" value={scenario.config_version || ''} onChange={e => updateScenario(key, { config_version: e.target.value })}>
                <option value="">Any</option>
                {(configVersions || []).map(v => (
                  <option key={v} value={v}>v{v}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Name & Description */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-body">
          <div style={{ marginBottom: '8px' }}>
            <label className="form-label">Report Name</label>
            <input className="form-input" value={def._name || ''} placeholder="e.g. This week vs last week"
              onChange={e => onChange({ ...def, _name: e.target.value })} />
          </div>
          <div>
            <label className="form-label">Description (optional)</label>
            <input className="form-input" value={def._description || ''} placeholder="What are you comparing?"
              onChange={e => onChange({ ...def, _description: e.target.value })} />
          </div>
          {isEdit && (
            <div style={{ marginTop: '8px' }}>
              <label className="form-label">Change Note (optional)</label>
              <input className="form-input" value={def._changeNote || ''} placeholder="What changed?"
                onChange={e => onChange({ ...def, _changeNote: e.target.value })} />
            </div>
          )}
        </div>
      </div>

      {/* Base Type */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-body">
          <label className="form-label">Compare using</label>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            {['funnel', 'time_series'].map(t => (
              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px' }}>
                <input type="radio" name="base_type" checked={baseType === t} onChange={() => updateBaseType(t)} />
                {t === 'funnel' ? 'Funnel' : 'Time Series'}
              </label>
            ))}
          </div>

          {/* Funnel step selection */}
          {baseType === 'funnel' && (
            <>
              <label className="form-label">Funnel Steps</label>
              {(baseDef.steps || []).map((step, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', minWidth: '20px' }}>{i + 1}.</span>
                  <span style={{ fontSize: '13px', flex: 1 }}>{step.label || step.step_id} <span style={{ color: 'var(--text-tertiary)' }}>({step.process_id})</span></span>
                  <button className="btn btn-sm" style={{ fontSize: '11px', padding: '2px 6px' }} onClick={() => removeFunnelStep(i)}>×</button>
                </div>
              ))}
              {/* Link identifier selectors */}
              {(baseDef.links || []).map((link, i) => {
                const fromStep = baseDef.steps[link.from_index];
                const toStep = baseDef.steps[link.to_index];
                const shared = sharedIdentifiers(fromStep, toStep, availableSteps);
                const allIds = [...new Set([...shared, ...getStepIdentifiers(fromStep, availableSteps), ...getStepIdentifiers(toStep, availableSteps)])];
                return (
                  <div key={`link-${i}`} style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', marginLeft: '28px' }}>
                    Link {link.from_index + 1} → {link.to_index + 1}:
                    <select className="form-select" style={{ display: 'inline', width: 'auto', marginLeft: '4px', fontSize: '12px', padding: '2px 4px' }}
                      value={link.identifier || ''} onChange={e => updateLinkIdentifier(i, e.target.value)}>
                      <option value="">Select identifier</option>
                      {allIds.map(id => <option key={id} value={id}>{id}</option>)}
                    </select>
                  </div>
                );
              })}
              <select className="form-select" style={{ marginTop: '8px', maxWidth: '300px' }} value="" onChange={e => {
                if (!e.target.value) return;
                const [pid, sid] = e.target.value.split('::');
                const step = allSteps.find(s => s.process_id === pid && s.step_id === sid);
                if (step) addFunnelStep(step);
                e.target.value = '';
              }}>
                <option value="">+ Add step…</option>
                {allSteps.map(s => (
                  <option key={`${s.process_id}::${s.step_id}`} value={`${s.process_id}::${s.step_id}`}>
                    {s.label || s.step_id} ({s.process_id})
                  </option>
                ))}
              </select>
            </>
          )}

          {/* Time-series series selection */}
          {baseType === 'time_series' && (
            <>
              <label className="form-label">Data Series</label>
              {(baseDef.series || []).map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', minWidth: '20px' }}>{i + 1}.</span>
                  <span style={{ fontSize: '13px', flex: 1 }}>{s.label || s.step_id} <span style={{ color: 'var(--text-tertiary)' }}>({s.process_id})</span></span>
                  <button className="btn btn-sm" style={{ fontSize: '11px', padding: '2px 6px' }} onClick={() => removeSeries(i)}>×</button>
                </div>
              ))}
              <select className="form-select" style={{ marginTop: '8px', maxWidth: '300px' }} value="" onChange={e => {
                if (!e.target.value) return;
                const [pid, sid] = e.target.value.split('::');
                const step = allSteps.find(s => s.process_id === pid && s.step_id === sid);
                if (step) addSeries(step);
                e.target.value = '';
              }}>
                <option value="">+ Add series…</option>
                {allSteps.map(s => (
                  <option key={`${s.process_id}::${s.step_id}`} value={`${s.process_id}::${s.step_id}`}>
                    {s.label || s.step_id} ({s.process_id})
                  </option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                <div>
                  <label className="form-label">Granularity</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['hour', 'day', 'week', 'month'].map(g => (
                      <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '13px', cursor: 'pointer' }}>
                        <input type="radio" name="comp_granularity" checked={baseDef.granularity === g}
                          onChange={() => updateBaseDef({ granularity: g })} />
                        {g.charAt(0).toUpperCase() + g.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="form-label">Chart Type</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['line', 'bar', 'area'].map(ct => (
                      <label key={ct} style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '13px', cursor: 'pointer' }}>
                        <input type="radio" name="comp_chart_type" checked={(baseDef.display?.chart_type || 'line') === ct}
                          onChange={() => updateBaseDef({ display: { ...baseDef.display, chart_type: ct } })} />
                        {ct.charAt(0).toUpperCase() + ct.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Scenario Configuration */}
      <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.05em', marginBottom: '8px' }}>
        Scenarios
      </div>

      {/* Quick presets */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <button className="btn btn-sm" onClick={() => applyPreset('last7')}>Last 7 days vs previous</button>
        <button className="btn btn-sm" onClick={() => applyPreset('last30')}>Last 30 days vs previous</button>
      </div>

      {renderScenarioFields('a', scenarios.a || {})}
      {renderScenarioFields('b', scenarios.b || {})}

      {/* Actions */}
      {saveError && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{saveError}</div>}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className="btn btn-primary" disabled={!canSave || saving} onClick={onSave}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Report'}
        </button>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function AnalyticsReportCreate() {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [mode, setMode] = useState(editId ? 'form' : null); // null = choose type, 'wizard', 'form', 'time_series', 'duration', 'active', 'comparison'
  const [reportType, setReportType] = useState(null); // 'funnel', 'time_series', 'duration', 'active', or 'comparison' — for type picker
  const [definition, setDefinition] = useState(null);
  const [availableSteps, setAvailableSteps] = useState(null);
  const [configVersions, setConfigVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');

  const isEdit = !!editId;

  const fetchAvailableSteps = useCallback(async () => {
    try {
      const res = await api.get('/portal/analytics/available-steps');
      setAvailableSteps(res.data.processes || []);
    } catch {
      // Non-critical
    }
  }, []);

  const fetchConfigVersions = useCallback(async () => {
    try {
      const res = await api.get('/portal/analytics/config-versions');
      setConfigVersions(res.data.versions || []);
    } catch {
      // Non-critical
    }
  }, []);

  // Load report for editing
  useEffect(() => {
    if (!editId) return;
    async function loadReport() {
      try {
        const res = await api.get(`/portal/analytics/reports/${editId}`);
        const report = res.data;
        const type = report.definition?.type || 'funnel';
        const def = type === 'time_series' ? (report.definition || emptyTimeSeriesDefinition())
          : type === 'duration' ? (report.definition || emptyDurationDefinition())
          : type === 'active' ? (report.definition || emptyActiveDefinition())
          : type === 'comparison' ? (report.definition || emptyComparisonDefinition())
          : (report.definition || emptyDefinition());
        // For duration edit, hydrate display values for SLA fields
        if (type === 'duration' && def.transitions) {
          def.transitions = def.transitions.map(t => ({
            ...t,
            _warning: secondsToDisplay(t.sla?.warning_seconds),
            _breach: secondsToDisplay(t.sla?.breach_seconds)
          }));
        }
        setDefinition({
          ...def,
          _name: report.name,
          _description: report.description || '',
          _changeNote: ''
        });
        setMode(type === 'time_series' ? 'time_series' : type === 'duration' ? 'duration' : type === 'active' ? 'active' : type === 'comparison' ? 'comparison' : 'form');
        fetchAvailableSteps();
        if (type === 'comparison') fetchConfigVersions();
      } catch (err) {
        setLoadError(err.response?.data?.message || 'Failed to load report for editing.');
      }
    }
    loadReport();
  }, [editId, fetchAvailableSteps, fetchConfigVersions]);

  function startCreate(builderMode) {
    setMode(builderMode);
    if (builderMode === 'time_series') {
      setDefinition({ ...emptyTimeSeriesDefinition(), _name: '', _description: '' });
    } else if (builderMode === 'duration') {
      setDefinition({ ...emptyDurationDefinition(), _name: '', _description: '' });
    } else if (builderMode === 'active') {
      setDefinition({ ...emptyActiveDefinition(), _name: '', _description: '' });
    } else if (builderMode === 'comparison') {
      setDefinition({ ...emptyComparisonDefinition(), _name: '', _description: '' });
      fetchConfigVersions();
    } else {
      setDefinition({ ...emptyDefinition(), _name: '', _description: '' });
    }
    setSaveError('');
    fetchAvailableSteps();
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');

    const { _name, _description, _changeNote, ...cleanDef } = definition;
    // Strip display-only fields from duration transitions
    if (cleanDef.transitions) {
      cleanDef.transitions = cleanDef.transitions.map(({ _warning, _breach, ...t }) => t);
    }
    // Strip empty scenario fields for comparison reports
    if (cleanDef.type === 'comparison' && cleanDef.scenarios) {
      for (const key of ['a', 'b']) {
        const s = cleanDef.scenarios[key];
        if (s) {
          if (!s.from) delete s.from;
          if (!s.to) delete s.to;
          if (!s.config_version) delete s.config_version;
        }
      }
    }

    try {
      if (editId) {
        await api.put(`/portal/analytics/reports/${editId}`, {
          name: _name.trim(),
          description: _description?.trim() || undefined,
          definition: cleanDef,
          change_note: _changeNote?.trim() || undefined
        });
        navigate(`/analytics/${editId}`);
      } else {
        const res = await api.post('/portal/analytics/reports', {
          name: _name.trim(),
          description: _description?.trim() || undefined,
          definition: cleanDef
        });
        navigate(`/analytics/${res.data.id}`);
      }
    } catch (err) {
      if (err.response?.data?.error === 'plan_limit_exceeded') {
        setSaveError(`Free plan is limited to ${err.response.data.max} report${err.response.data.max !== 1 ? 's' : ''}. Upgrade to create more.`);
      } else {
        setSaveError(err.response?.data?.message || 'Failed to save report.');
      }
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (editId) {
      navigate(`/analytics/${editId}`);
    } else {
      navigate('/analytics');
    }
  }

  if (!isAdmin) {
    return (
      <div className="page">
        <Link to="/analytics" style={{ fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}>← Back to Analytics</Link>
        <div className="alert alert-error" style={{ marginTop: '12px' }}>Only admins can create or edit reports.</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page">
        <Link to="/analytics" style={{ fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}>← Back to Analytics</Link>
        <div className="alert alert-error" style={{ marginTop: '12px' }}>{loadError}</div>
      </div>
    );
  }

  // Edit mode — loading report
  if (editId && !definition) {
    return (
      <div className="page">
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
      </div>
    );
  }

  // Choose type & mode (create only)
  if (!mode && !editId) {
    return (
      <div className="page">
        <div style={{ marginBottom: '4px' }}>
          <Link to="/analytics" style={{ fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}>← Back to Analytics</Link>
        </div>
        <div className="page-header">
          <h1 className="page-title">Create Report</h1>
          <p className="page-subtitle">Choose a report type to get started.</p>
        </div>

        {/* Type selection */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div className="card" style={{ flex: '1 1 140px', cursor: 'pointer', border: '2px solid var(--border)' }}
            onClick={() => setReportType('funnel')}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = reportType === 'funnel' ? 'var(--accent)' : 'var(--border)'}
            ref={el => { if (el && reportType === 'funnel') el.style.borderColor = 'var(--accent)'; }}>
            <div className="card-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#x1F4CA;</div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Funnel Report</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Step-by-step conversion funnel — track entities through process steps.
              </div>
            </div>
          </div>
          <div className="card" style={{ flex: '1 1 140px', cursor: 'pointer', border: '2px solid var(--border)' }}
            onClick={() => setReportType('time_series')}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = reportType === 'time_series' ? 'var(--accent)' : 'var(--border)'}
            ref={el => { if (el && reportType === 'time_series') el.style.borderColor = 'var(--accent)'; }}>
            <div className="card-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#x1F4C8;</div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Time-Series Report</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Event volume over time — monitor trends and compare steps.
              </div>
            </div>
          </div>
          <div className="card" style={{ flex: '1 1 140px', cursor: 'pointer', border: '2px solid var(--border)' }}
            onClick={() => setReportType('duration')}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = reportType === 'duration' ? 'var(--accent)' : 'var(--border)'}
            ref={el => { if (el && reportType === 'duration') el.style.borderColor = 'var(--accent)'; }}>
            <div className="card-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#x23F1;&#xFE0F;</div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Duration / SLA Report</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Measure time between steps — track SLA compliance and identify bottlenecks.
              </div>
            </div>
          </div>
          <div className="card" style={{ flex: '1 1 140px', cursor: 'pointer', border: '2px solid var(--border)' }}
            onClick={() => setReportType('active')}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = reportType === 'active' ? 'var(--accent)' : 'var(--border)'}
            ref={el => { if (el && reportType === 'active') el.style.borderColor = 'var(--accent)'; }}>
            <div className="card-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#x1F6A7;</div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Active / WIP Report</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Track in-flight entities — see where work is stuck and identify stale items.
              </div>
            </div>
          </div>
          <div className="card" style={{ flex: '1 1 140px', cursor: 'pointer', border: '2px solid var(--border)' }}
            onClick={() => setReportType('comparison')}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = reportType === 'comparison' ? 'var(--accent)' : 'var(--border)'}
            ref={el => { if (el && reportType === 'comparison') el.style.borderColor = 'var(--accent)'; }}>
            <div className="card-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#x2696;&#xFE0F;</div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Comparison Report</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Compare time periods or config versions — measure impact and spot regressions.
              </div>
            </div>
          </div>
        </div>

        {/* Mode selection (funnel only) */}
        {reportType === 'funnel' && (
          <>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Build mode
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div className="card" style={{ flex: 1, cursor: 'pointer' }} onClick={() => startCreate('wizard')}>
                <div className="card-body" style={{ textAlign: 'center', padding: '24px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Guided Setup</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Step-by-step wizard — best for your first report.
                  </div>
                </div>
              </div>
              <div className="card" style={{ flex: 1, cursor: 'pointer' }} onClick={() => startCreate('form')}>
                <div className="card-body" style={{ textAlign: 'center', padding: '24px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Form</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    All settings on one page — faster for experienced users.
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Time-series, duration, active, and comparison go straight to form */}
        {(reportType === 'time_series' || reportType === 'duration' || reportType === 'active' || reportType === 'comparison') && (
          <div style={{ textAlign: 'center' }}>
            <button className="btn btn-primary" onClick={() => startCreate(reportType)}>
              Continue
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ marginBottom: '4px' }}>
        <Link to="/analytics" style={{ fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}>← Back to Analytics</Link>
      </div>
      <div className="page-header" style={{ marginBottom: '16px' }}>
        <h1 className="page-title">{isEdit ? 'Edit Report' : 'Create Report'}</h1>
      </div>
      {mode === 'comparison' ? (
        <ComparisonFormBuilder
          availableSteps={availableSteps}
          definition={definition}
          onChange={setDefinition}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          error={saveError}
          isEdit={isEdit}
          configVersions={configVersions}
        />
      ) : mode === 'active' ? (
        <ActiveFormBuilder
          availableSteps={availableSteps}
          definition={definition}
          onChange={setDefinition}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          error={saveError}
          isEdit={isEdit}
        />
      ) : mode === 'duration' ? (
        <DurationFormBuilder
          availableSteps={availableSteps}
          definition={definition}
          onChange={setDefinition}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          error={saveError}
          isEdit={isEdit}
        />
      ) : mode === 'time_series' ? (
        <TimeSeriesFormBuilder
          availableSteps={availableSteps}
          definition={definition}
          onChange={setDefinition}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          error={saveError}
          isEdit={isEdit}
        />
      ) : mode === 'wizard' ? (
        <WizardBuilder
          availableSteps={availableSteps}
          definition={definition}
          onChange={setDefinition}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          error={saveError}
        />
      ) : (
        <FormBuilder
          availableSteps={availableSteps}
          definition={definition}
          onChange={setDefinition}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          error={saveError}
          isEdit={isEdit}
        />
      )}
    </div>
  );
}
